import clsx from 'clsx';
import { Button } from 'flowbite-react';
import { useMemo, useState, type PropsWithChildren } from 'react';
import { IconButton } from '#common/components/IconButton.tsx';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { type TakeModel } from '#speaker-shop/components/RecordingContext.tsx';
import { hasLoadedAudio, isRestoredTake } from '#speaker-shop/utils/recordingStorage.ts';
import { Checkbox } from './Checkbox';
import { PlayButton } from './PlayButton';

export type TakeRowModel = TakeModel | { isPlaceholder: true; text: string };

interface Props {
    take: TakeRowModel;
    setIsChecked: (isChecked: boolean) => void;
    onDelete: () => void;
    index: number;
}

function TableColumn({ children, className }: PropsWithChildren<{ className?: string }>) {
    return <td className={clsx('p-4 text-gray-900', className)}>{children}</td>;
}

export function TakeRow({ take, setIsChecked, onDelete, index }: Props) {
    const { t, rawT } = useEditableTranslation('checkout');
    const isPlaceholder = 'isPlaceholder' in take;
    const [ showDeleteConfirm, setShowDeleteConfirm ] = useState(false);
    const hasAudio = useMemo(() => isPlaceholder ? false : hasLoadedAudio(take.audio), [ isPlaceholder, take ]);
    const isRestored = useMemo(() => isPlaceholder ? false : isRestoredTake(take), [ isPlaceholder, take ]);

    return (
        <tr className='text-xl relative' data-take-index={index}>
            <TableColumn className='w-32'>{isPlaceholder ? '' : take.name}</TableColumn>
            <TableColumn>
                <span data-take-text className='content'>{take.text}</span>
            </TableColumn>
            <TableColumn className='w-16'>
                <div className='flex justify-self-center gap-x-4'>
                    <PlayButton
                        audio={isPlaceholder ? '' : take.audio}
                        stopTitle={rawT('stop')}
                        playTitle={rawT('play')}
                        takeId={isPlaceholder ? '' : take.takeId}
                    />
                    <IconButton
                        icon='heroicons--trash'
                        className={clsx(
                            (hasAudio || isRestored)
                                ? 'cursor-pointer text-red-600'
                                : 'cursor-not-allowed text-slate-500'
                        )}
                        onClick={() => setShowDeleteConfirm(true)}
                    />
                </div>
            </TableColumn>

            <TableColumn className='w-16'>
                <Checkbox
                    isChecked={isPlaceholder ? false : take.isChecked}
                    onChange={setIsChecked}
                    label=''
                    uncheckedOnlyBorder={true}
                    className={clsx(
                        'text-xl items-center flex justify-self-center',
                        (hasAudio || isRestored) ? 'text-crispybluedark-700' : 'cursor-not-allowed text-slate-500'
                    )}
                />
            </TableColumn>
            <td className='w-0'>
                {showDeleteConfirm && (
                    <div className='absolute inset-0 backdrop-blur-sm flex flex-col items-center justify-center z-10 text-base px-4'>
                        <div className='flex flex-row justify-end w-full'>
                            <div className='flex flex-row gap-4 items-center '>
                                <div>{t('confirmDeleteTake')}</div>
                                <Button
                                    className='px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700'
                                    onClick={() => {
                                        onDelete();
                                        setShowDeleteConfirm(false);
                                    }}
                                >
                                    {t('delete')}
                                </Button>
                                <Button
                                    className='px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400'
                                    onClick={() => setShowDeleteConfirm(false)}
                                >
                                    {t('cancel')}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </td>
        </tr>
    );
}

interface MobileTakeRowProps {
    take: TakeRowModel;
    setIsChecked: (isChecked: boolean) => void;
    onDelete: () => void;
    index: number;
}
export function MobileTakeRow({ take, setIsChecked, onDelete, index }: MobileTakeRowProps) {
    const { t, rawT } = useEditableTranslation('checkout');

    const isPlaceholder = 'isPlaceholder' in take;
    const [ showDeleteConfirm, setShowDeleteConfirm ] = useState(false);
    const hasAudio = useMemo(() => isPlaceholder ? false : hasLoadedAudio(take.audio), [ isPlaceholder, take ]);
    const isRestored = useMemo(() => isPlaceholder ? false : isRestoredTake(take), [ isPlaceholder, take ]);

    return (
        <div className='border border-gray-200 rounded-lg p-4 gap-2 flex flex-col relative' data-take-index={index}>
            {showDeleteConfirm && (
                <div className='absolute inset-0 backdrop-blur-sm flex flex-col items-center justify-center z-10 text-base px-4 m-1'>
                    <div className='flex flex-row justify-end w-full'>
                        <div className='flex flex-row gap-4 items-center '>
                            <div>{t('confirmDeleteTake')}</div>
                            <Button
                                className='px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700'
                                onClick={() => {
                                    onDelete();
                                    setShowDeleteConfirm(false);
                                }}
                            >
                                {t('delete')}
                            </Button>
                            <Button
                                className='px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400'
                                onClick={() => setShowDeleteConfirm(false)}
                            >
                                {t('cancel')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            <div className='flex flex-row gap-2'>
                <PlayButton
                    audio={isPlaceholder ? '' : take.audio}
                    stopTitle={rawT('stop')}
                    playTitle={rawT('play')}
                    takeId={isPlaceholder ? '' : take.takeId}
                />
                {isPlaceholder ? '\u00a0' : take.name}
                <div className='grow' />
                <Checkbox
                    isChecked={isPlaceholder ? false : take.isChecked}
                    onChange={setIsChecked}
                    uncheckedOnlyBorder={true}
                    labelPosition='left'
                    label='kaufen'
                    className={clsx(
                        (hasAudio || isRestored) ? 'text-crispybluedark-700' : 'cursor-not-allowed text-slate-500'
                    )}
                />
                <IconButton
                    icon='heroicons--trash'
                    className={clsx(
                        'justify-self-center',
                        (hasAudio || isRestored) ? 'cursor-pointer text-red-600' : 'cursor-not-allowed text-slate-500'
                    )}
                    onClick={() => setShowDeleteConfirm(true)}
                />
            </div>
            <div>
                <div className='text-xs'>Text:</div>
                <span data-take-text className='content'>{take.text}</span>
            </div>
        </div>
    );
}
